import {
  Text,
  Flex,
  Dialog,
  CloseIcon,
  DialogFooter,
} from "@fluentui/react-northstar";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Devices } from "../../../types/Device.types";
import { ConnectedStartAudioTestButton } from "../results-start-audio-test-button/ConnectedStartAudioTestButton";
import { ConnectedCamSelectDropdown } from "../results-device-select-dropdown/ConnectedCamSelectDropdown";
import { ConnectedSpeakerSelectDropdown } from "../results-device-select-dropdown/ConnectedSpeakerSelectDropdown";
import { ConnectedMicSelectDropdown } from "../results-device-select-dropdown/ConnectedMicSelectDropdown";

import { DialogIcon } from "./dialog-icon/DialogIcon";
import { useDeviceStatusDialogBoxStyles } from "./DeviceStatusDialogBox.styles";
import { DeviceStatusDialogBoxProps } from "./DeviceStatusDialogBox.types";

export const DeviceStatusDialogBox: React.FC<DeviceStatusDialogBoxProps> = ({
  deviceName,
  isDialogOpen,
  closeDialog,
  isMobile,
  status,
  statusDetail,
}) => {
  const deviceStatusViewStyles = useDeviceStatusDialogBoxStyles();
  const { t } = useTranslation();

  const deviceNameString = useMemo(() => {
    switch (deviceName) {
      case Devices.Camera:
        return t("CAMERA_LOWERCASE");
      case Devices.Speaker:
        return t("SPEAKER_LOWERCASE");
      case Devices.Mic:
        return t("MICROPHONE_LOWERCASE");
    }
  }, [deviceName, t]);

  const chooseDeviceDropdown = () => {
    if (deviceName === Devices.Speaker) {
      return (
        <ConnectedSpeakerSelectDropdown
          label={t("SELECT_DEVICE_LABEL", { deviceName: deviceNameString })}
        />
      );
    } else if (deviceName === Devices.Camera) {
      return (
        <ConnectedCamSelectDropdown
          label={t("SELECT_DEVICE_LABEL", { deviceName: deviceNameString })}
        />
      );
    } else if (deviceName === Devices.Mic) {
      return (
        <ConnectedMicSelectDropdown
          label={t("SELECT_DEVICE_LABEL", { deviceName: deviceNameString })}
        />
      );
    } else {
      return undefined;
    }
  };

  return (
    <Dialog
      aria-labelledby="dialog-header"
      aria-describedby="dialog-description"
      styles={deviceStatusViewStyles.dialogBox}
      open={isDialogOpen}
      closeOnOutsideClick
      data-testid="deviceStatusDialogBox"
      headerAction={{
        icon: (
          <CloseIcon
            role="button"
            size="large"
            styles={deviceStatusViewStyles.closeIcon}
          />
        ),
        title: "Close Dialog",
        onClick: closeDialog,
      }}
      content={
        <Flex column vAlign="start" hAlign="center" gap="gap.medium">
          <DialogIcon status={status} />

          <Flex column gap="gap.large">
            <Flex column gap="gap.small">
              <Text
                id="dialog-header"
                aria-live="assertive"
                as="h1"
                styles={deviceStatusViewStyles.headingLabel}
                size="larger"
                weight="bold"
                align="center"
                content={statusDetail.header}
              />
              <Text
                id="dialog-description"
                aria-live="assertive"
                styles={deviceStatusViewStyles.descriptionLabel}
                size="large"
                weight="regular"
                align="center"
                content={statusDetail.body}
              />
            </Flex>
            <Flex hAlign="center">{!isMobile && chooseDeviceDropdown()}</Flex>
          </Flex>
        </Flex>
      }
      footer={
        <DialogFooter>
          {deviceName === Devices.Speaker && (
            <Flex hAlign="center">
              <ConnectedStartAudioTestButton />
            </Flex>
          )}
        </DialogFooter>
      }
    />
  );
};
